import { useQuery, useMutation } from "@tanstack/react-query";
import { useState, useMemo } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Progress } from "@/components/ui/progress";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import { apiRequest, queryClient } from "@/lib/queryClient";
import type { Goal } from "@shared/schema";
import {
  Dumbbell, Footprints, Scale, Activity, Moon, Heart,
  Plus, Target, TrendingUp, TrendingDown, Minus, Pencil, Trash2, ExternalLink, Share2
} from "lucide-react";
import { Link } from "wouter";
import ShareDialog from "@/components/share-dialog";

// ─── Metric definitions ────────────────────────────────────────────────────

export const FITNESS_METRICS = [
  { key: "workouts",       label: "Workouts",          unit: "sessions", color: "#f97316", Icon: Dumbbell,   description: "Workout sessions completed per day" },
  { key: "steps",          label: "Steps",              unit: "steps",    color: "#22c55e", Icon: Footprints, description: "Daily step count target" },
  { key: "weight",         label: "Target Weight",      unit: "kg",       color: "#3b82f6", Icon: Scale,      description: "Goal body weight (trend toward target)" },
  { key: "bodyFat",        label: "Body Fat %",         unit: "%",        color: "#a855f7", Icon: Activity,   description: "Target body fat percentage" },
  { key: "sleepHours",     label: "Sleep",              unit: "hrs",      color: "#6366f1", Icon: Moon,       description: "Hours of sleep per night" },
  { key: "heartRate",      label: "Resting Heart Rate", unit: "bpm",      color: "#ef4444", Icon: Heart,      description: "Target resting heart rate" },
  { key: "caloriesBurned", label: "Calories Burned",    unit: "kcal",     color: "#f59e0b", Icon: Activity,   description: "Daily active calories burned" },
  { key: "bloodSugar",     label: "Blood Sugar",        unit: "mg/dL",    color: "#ec4899", Icon: Heart,      description: "Target fasting blood sugar level" },
  { key: "chestCm",        label: "Chest",              unit: "cm",       color: "#10b981", Icon: Scale,      description: "Chest measurement target" },
  { key: "waistCm",        label: "Waist",              unit: "cm",       color: "#f43f5e", Icon: Scale,      description: "Waist measurement target" },
  { key: "hipsCm",         label: "Hips",               unit: "cm",       color: "#8b5cf6", Icon: Scale,      description: "Hips measurement target" },
  { key: "armsCm",         label: "Arms",               unit: "cm",       color: "#06b6d4", Icon: Scale,      description: "Arms measurement target" },
  { key: "thighsCm",       label: "Thighs",             unit: "cm",       color: "#84cc16", Icon: Scale,      description: "Thighs measurement target" },
  { key: "heightCm",       label: "Height",             unit: "cm",       color: "#64748b", Icon: Scale,      description: "Height tracking (cm)" },
] as const;

export type FitnessMetricKey = (typeof FITNESS_METRICS)[number]["key"];

// For weight/heart-rate/waist/blood-sugar the goal is to decrease (lower = better)
export const LOWER_IS_BETTER = new Set<FitnessMetricKey>(["weight", "bodyFat", "heartRate", "waistCm", "bloodSugar"]);

// ─── Sparkline ─────────────────────────────────────────────────────────────

function SparkLine({ data, target, color, lowerIsBetter }: { data: (number | null)[]; target: number; color: string; lowerIsBetter?: boolean }) {
  const filled = data.map(v => v ?? 0);
  if (filled.length < 2) return null;
  const W = 220, H = 56, P = 4;
  const maxV = Math.max(target * 1.4, ...filled, 1);
  const y = (v: number) => P + (H - P * 2) * (1 - v / maxV);
  const xStep = (W - P * 2) / Math.max(filled.length - 1, 1);
  const pts = filled.map((v, i) => `${P + i * xStep},${y(v)}`).join(" ");
  const ty = y(target);
  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-14" aria-hidden>
      <line x1={P} y1={ty} x2={W - P} y2={ty} stroke={color} strokeDasharray="4,2" strokeOpacity={0.35} strokeWidth={1.5} />
      <polyline fill="none" stroke={color} strokeWidth={2} strokeLinejoin="round" points={pts} />
      {filled.map((v, i) => (
        data[i] !== null
          ? <circle key={i} cx={P + i * xStep} cy={y(v)} r={3} fill={color} stroke="white" strokeWidth={1.5} />
          : <circle key={i} cx={P + i * xStep} cy={y(v)} r={2.5} fill="none" stroke={color} strokeWidth={1} strokeDasharray="2,1" />
      ))}
    </svg>
  );
}

// ─── Trend badge ───────────────────────────────────────────────────────────

function TrendBadge({ data, lowerIsBetter }: { data: (number | null)[]; lowerIsBetter?: boolean }) {
  const vals = data.filter(v => v !== null) as number[];
  if (vals.length < 4) return null;
  const recent = vals.slice(-Math.ceil(vals.length / 2));
  const prev = vals.slice(0, Math.floor(vals.length / 2));
  if (prev.length === 0) return null;
  const avg = recent.reduce((a, b) => a + b, 0) / recent.length;
  const prevAvg = prev.reduce((a, b) => a + b, 0) / prev.length;
  const pct = prevAvg === 0 ? 0 : ((avg - prevAvg) / prevAvg) * 100;
  if (Math.abs(pct) < 1) return <span className="text-xs text-muted-foreground flex items-center gap-1"><Minus className="w-3 h-3" />Stable</span>;
  const isGood = lowerIsBetter ? pct < 0 : pct > 0;
  return isGood
    ? <span className="text-xs text-green-600 flex items-center gap-1"><TrendingUp className="w-3 h-3" />{lowerIsBetter ? "" : "+"}{pct.toFixed(0)}%</span>
    : <span className="text-xs text-orange-500 flex items-center gap-1"><TrendingDown className="w-3 h-3" />{pct.toFixed(0)}%</span>;
}

// ─── Day labels ────────────────────────────────────────────────────────────

function DayLabels({ days }: { days: number }) {
  const labels: string[] = [];
  const DAY = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
  for (let i = days - 1; i >= 0; i--) {
    const d = new Date(); d.setDate(d.getDate() - i);
    labels.push(i === 0 ? "Today" : DAY[d.getDay()]);
  }
  return (
    <div className="flex justify-between px-1 mt-0.5">
      {labels.map((l, i) => <span key={i} className="text-[9px] text-muted-foreground">{l}</span>)}
    </div>
  );
}

// ─── Goal Card ─────────────────────────────────────────────────────────────

interface GoalCardProps {
  goal: Goal;
  todayValue: number | null;
  trendData: (number | null)[];
  onEdit: (g: Goal) => void;
  onDelete: (id: string) => void;
  onShare: (g: Goal) => void;
}

function GoalCard({ goal, todayValue, trendData, onEdit, onDelete, onShare }: GoalCardProps) {
  const metric = FITNESS_METRICS.find(m => m.key === goal.healthMetric);
  if (!metric) return null;

  const { Icon, color, unit, label } = metric;
  const target = goal.customGoalTarget ?? 0;
  const lowerIsBetter = LOWER_IS_BETTER.has(metric.key);
  const today = todayValue ?? 0;
  const hasToday = todayValue !== null;

  let pct = 0;
  let statusLabel = "";
  let statusVariant: "default" | "destructive" | "outline" | "secondary" = "outline";

  if (hasToday && target > 0) {
    if (lowerIsBetter) {
      pct = today <= target ? 100 : Math.max(0, 100 - ((today - target) / target) * 100);
      statusLabel = today <= target ? "On Track" : "Above target";
      statusVariant = today <= target ? "default" : "destructive";
    } else {
      pct = Math.min(100, (today / target) * 100);
      statusLabel = pct >= 90 ? "On Track" : `${pct.toFixed(0)}%`;
      statusVariant = pct >= 90 ? "default" : "outline";
    }
  }

  const filled = trendData.filter(v => v !== null);

  return (
    <Card
      className="cursor-pointer hover:shadow-md transition-shadow"
      onClick={() => onEdit(goal)}
    >
      <CardHeader className="pb-2 pt-4 px-4">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-2">
            <div className="p-2 rounded-lg" style={{ backgroundColor: color + "18" }}>
              <Icon className="w-4 h-4" style={{ color }} />
            </div>
            <div>
              <div className="font-semibold text-sm leading-tight">{goal.title}</div>
              <div className="text-xs text-muted-foreground">{label}</div>
            </div>
          </div>
          <div className="flex gap-1" onClick={e => e.stopPropagation()}>
            <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => onShare(goal)}>
              <Share2 className="w-3.5 h-3.5" />
            </Button>
            <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => onEdit(goal)}>
              <Pencil className="w-3.5 h-3.5" />
            </Button>
            <Button size="icon" variant="ghost" className="h-7 w-7 text-destructive hover:text-destructive" onClick={() => onDelete(goal.id)}>
              <Trash2 className="w-3.5 h-3.5" />
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="px-4 pb-4 space-y-3">
        <div className="flex items-end justify-between">
          <div>
            {hasToday ? (
              <>
                <span className="text-2xl font-bold" style={{ color }}>
                  {today % 1 === 0 ? today : today.toFixed(1)}
                </span>
                <span className="text-sm text-muted-foreground ml-1">{unit}</span>
              </>
            ) : (
              <span className="text-sm text-muted-foreground italic">No data today</span>
            )}
          </div>
          <div className="text-right">
            <div className="text-sm font-medium">
              {lowerIsBetter ? "Target ≤" : "Target:"} {target}{unit}
            </div>
            {hasToday && statusLabel && (
              <div className="mt-0.5">
                <Badge
                  variant={statusVariant}
                  className={`text-[10px] h-4 px-1 ${statusVariant === "default" ? "bg-green-500" : ""}`}
                >
                  {statusLabel}
                </Badge>
              </div>
            )}
          </div>
        </div>

        {hasToday && (
          <Progress value={pct} className="h-2" />
        )}

        <div>
          <div className="flex items-center justify-between mb-1">
            <span className="text-[10px] text-muted-foreground uppercase tracking-wide">7-day trend</span>
            {filled.length >= 4 ? (
              <TrendBadge data={trendData} lowerIsBetter={lowerIsBetter} />
            ) : (
              <span className="text-[10px] text-muted-foreground">{filled.length} day{filled.length !== 1 ? "s" : ""} logged</span>
            )}
          </div>
          <SparkLine data={trendData} target={target} color={color} lowerIsBetter={lowerIsBetter} />
          <DayLabels days={trendData.length} />
        </div>
      </CardContent>
    </Card>
  );
}

// ─── Create / Edit dialog ──────────────────────────────────────────────────

export interface DialogState { open: boolean; goal: Goal | null; initialMetric?: FitnessMetricKey }

export function GoalDialog({
  state, onClose, usedMetrics,
}: {
  state: DialogState; onClose: () => void; usedMetrics: Set<string>;
}) {
  const { toast } = useToast();
  const isEdit = !!state.goal;

  const [metric, setMetric] = useState<FitnessMetricKey>(
    (state.goal?.healthMetric as FitnessMetricKey) ?? state.initialMetric ?? "workouts"
  );
  const [target, setTarget] = useState<string>(state.goal?.customGoalTarget?.toString() ?? "");
  const [title, setTitle] = useState<string>(state.goal?.title ?? "");
  const [showOnGoalPage, setShowOnGoalPage] = useState<boolean>(
    (state.goal as { showOnMainGoalPage?: boolean } | null)?.showOnMainGoalPage ?? true
  );

  const metricInfo = FITNESS_METRICS.find(m => m.key === metric)!;
  const lowerIsBetter = LOWER_IS_BETTER.has(metric);

  const createMutation = useMutation({
    mutationFn: (data: any) => apiRequest("POST", "/api/goals", data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/goals"] });
      toast({ title: "Fitness goal created" });
      onClose();
    },
    onError: () => {
      toast({ title: "Couldn't save the fitness goal", description: "Please try again.", variant: "destructive" });
    },
  });

  const updateMutation = useMutation({
    mutationFn: (data: any) => apiRequest("PATCH", `/api/goals/${state.goal!.id}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/goals"] });
      toast({ title: "Fitness goal updated" });
      onClose();
    },
    onError: () => {
      toast({ title: "Couldn't save the fitness goal", description: "Please try again.", variant: "destructive" });
    },
  });

  const handleSave = () => {
    const t = parseFloat(target);
    if (!metric || isNaN(t) || t <= 0) {
      toast({ title: "Enter a valid target", variant: "destructive" });
      return;
    }
    const autoTitle = title.trim() || `${metricInfo.label} Goal`;
    const payload = {
      title: autoTitle,
      // The goals API requires a category and a (nullable) deadline; fitness
      // goals are open-ended health goals, so use the Health category and no
      // deadline. Omitting these made the server reject the save silently.
      category: "Health",
      deadline: null,
      showOnMainGoalPage: showOnGoalPage,
      goalSource: "fitness",
      healthMetric: metric,
      customGoalTarget: t,
      customGoalType: "number",
      customGoalTitle: `${metricInfo.label} (${metricInfo.unit})`,
      progressCalculationType: "custom",
      status: "active",
    };
    if (isEdit) updateMutation.mutate(payload);
    else createMutation.mutate(payload);
  };

  const isPending = createMutation.isPending || updateMutation.isPending;

  return (
    <Dialog open={state.open} onOpenChange={o => !o && onClose()}>
      <DialogContent >
        <DialogHeader>
          <DialogTitle>{isEdit ? "Edit Fitness Goal" : "Create Fitness Goal"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="space-y-1.5">
            <Label>Metric to track</Label>
            <Select value={metric} onValueChange={v => {
              setMetric(v as FitnessMetricKey);
              setTitle("");
            }}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {FITNESS_METRICS.map(m => {
                  const alreadyUsed = !isEdit && usedMetrics.has(m.key);
                  return (
                    <SelectItem key={m.key} value={m.key} disabled={alreadyUsed}>
                      {m.label} ({m.unit}){alreadyUsed ? " — already tracked" : ""}
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>
            {metricInfo && (
              <p className="text-xs text-muted-foreground">{metricInfo.description}</p>
            )}
          </div>

          <div className="space-y-1.5">
            <Label>
              {lowerIsBetter ? "Target (lower is better)" : "Daily target"} ({metricInfo.unit})
            </Label>
            <Input
              type="number"
              min={0}
              step={metric === "steps" ? 500 : metric === "weight" ? 0.5 : metric === "heartRate" ? 1 : 0.5}
              value={target}
              onChange={e => setTarget(e.target.value)}
              placeholder={
                metric === "steps" ? "e.g. 10000"
                : metric === "weight" ? "e.g. 75"
                : metric === "workouts" ? "e.g. 1"
                : metric === "sleepHours" ? "e.g. 8"
                : metric === "heartRate" ? "e.g. 60"
                : "e.g. 20"
              }
            />
          </div>

          <div className="space-y-1.5">
            <Label>Goal name <span className="text-muted-foreground">(optional)</span></Label>
            <Input
              value={title}
              onChange={e => setTitle(e.target.value)}
              placeholder={`${metricInfo.label} Goal`}
            />
          </div>

          <div className="flex items-center justify-between rounded-md border px-3 py-2.5">
            <div className="space-y-0.5 pr-3">
              <Label htmlFor="toggle-show-on-goal-page">Show on main Goals page</Label>
              <p className="text-xs text-muted-foreground">
                If on, this goal is also listed under <strong>Health</strong> on the Goals page.
              </p>
            </div>
            <Switch
              id="toggle-show-on-goal-page"
              checked={showOnGoalPage}
              onCheckedChange={setShowOnGoalPage}
              data-testid="switch-show-on-goal-page"
            />
          </div>

          <div className="rounded-md bg-muted/50 px-3 py-2 text-xs text-muted-foreground">
            Progress syncs from your <strong>Workout Planner</strong> and <strong>Health Metrics</strong> logs.
          </div>

          <div className="flex gap-2 justify-end pt-1">
            <Button variant="outline" onClick={onClose} disabled={isPending}>Cancel</Button>
            <Button onClick={handleSave} disabled={isPending}>
              {isPending ? "Saving…" : isEdit ? "Save changes" : "Create goal"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ─── Today's snapshot ──────────────────────────────────────────────────────

interface FitnessDayData {
  date: string;
  workouts: number;
  weightKg: number | null;
  bodyFatPct: number | null;
  sleepHours: number | null;
  stepsCount: number | null;
  restingHeartRate: number | null;
  bloodSugar: number | null;
  caloriesBurned: number | null;
  heightCm: number | null;
  chestCm: number | null;
  waistCm: number | null;
  hipsCm: number | null;
  armsCm: number | null;
  thighsCm: number | null;
}

const METRIC_TO_FIELD: Record<FitnessMetricKey, keyof FitnessDayData> = {
  workouts: "workouts",
  steps: "stepsCount",
  weight: "weightKg",
  bodyFat: "bodyFatPct",
  sleepHours: "sleepHours",
  heartRate: "restingHeartRate",
  bloodSugar: "bloodSugar",
  caloriesBurned: "caloriesBurned",
  chestCm: "chestCm",
  waistCm: "waistCm",
  hipsCm: "hipsCm",
  armsCm: "armsCm",
  thighsCm: "thighsCm",
  heightCm: "heightCm",
};

function TodayPanel({ today, trackedKeys }: { today: FitnessDayData | null; trackedKeys: Set<string> }) {
  if (!today || trackedKeys.size === 0) return null;
  const tracked = FITNESS_METRICS.filter(m => trackedKeys.has(m.key));
  const hasAny = tracked.some(m => today[METRIC_TO_FIELD[m.key]] !== null);
  if (!hasAny) return null;

  return (
    <Card className="mb-6">
      <CardHeader className="pb-2 pt-4 px-4">
        <div className="flex items-center gap-2">
          <Target className="w-4 h-4 text-primary" />
          <span className="font-semibold text-sm">Today's Snapshot</span>
          <Badge variant="outline" className="text-[10px] ml-auto">
            {new Date().toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" })}
          </Badge>
        </div>
      </CardHeader>
      <CardContent className="px-4 pb-4">
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
          {tracked.map(({ key, label, unit, color, Icon }) => {
            const val = today[METRIC_TO_FIELD[key]];
            if (val === null) return null;
            return (
              <div key={key} className="flex items-center gap-2 p-2 rounded-lg" style={{ backgroundColor: color + "10" }}>
                <Icon className="w-4 h-4 flex-shrink-0" style={{ color }} />
                <div>
                  <div className="text-xs text-muted-foreground">{label}</div>
                  <div className="font-semibold text-sm">
                    {typeof val === "number" && val % 1 === 0 ? val : typeof val === "number" ? val.toFixed(1) : val}
                    <span className="text-xs font-normal text-muted-foreground ml-0.5">{unit}</span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}

// ─── Page ──────────────────────────────────────────────────────────────────

export default function FitnessGoalPage() {
  const { toast } = useToast();
  const [dialogState, setDialogState] = useState<DialogState>({ open: false, goal: null });
  const [sharingGoal, setSharingGoal] = useState<Goal | null>(null);

  const { data: allGoals = [], isLoading: goalsLoading } = useQuery<Goal[]>({
    queryKey: ["/api/goals"],
  });

  const { data: trends = [], isLoading: trendsLoading } = useQuery<FitnessDayData[]>({
    queryKey: ["/api/health/fitness/trends"],
    queryFn: () => fetch("/api/health/fitness/trends?days=7").then(r => r.json()),
  });

  const fitnessGoals = useMemo(
    () => allGoals.filter(g => (g as any).goalSource === "fitness"),
    [allGoals]
  );

  const usedMetrics = useMemo(
    () => new Set(fitnessGoals.map(g => (g as any).healthMetric as string).filter(Boolean)),
    [fitnessGoals]
  );

  const today: FitnessDayData | null = trends.length > 0 ? trends[trends.length - 1] : null;

  const getTrend = (metricKey: FitnessMetricKey): (number | null)[] => {
    const field = METRIC_TO_FIELD[metricKey];
    return trends.map(d => {
      const v = d[field];
      return v === null || v === undefined ? null : Number(v);
    });
  };

  const getTodayVal = (metricKey: FitnessMetricKey): number | null => {
    if (!today) return null;
    const v = today[METRIC_TO_FIELD[metricKey]];
    return v === null || v === undefined ? null : Number(v);
  };

  const deleteMutation = useMutation({
    mutationFn: (id: string) => apiRequest("DELETE", `/api/goals/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/goals"] });
      toast({ title: "Fitness goal deleted" });
    },
    onError: () => {
      toast({ title: "Couldn't delete the fitness goal", description: "Please try again.", variant: "destructive" });
    },
  });

  const openCreate = () => setDialogState({ open: true, goal: null });
  const openEdit = (g: Goal) => setDialogState({ open: true, goal: g });
  const closeDialog = () => setDialogState({ open: false, goal: null });

  const isLoading = goalsLoading || trendsLoading;

  return (
    <div className="flex-1 overflow-y-auto">
      <div className="w-full px-4 py-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-xl bg-orange-100 dark:bg-orange-900/30">
              <Dumbbell className="w-6 h-6 text-orange-600" />
            </div>
            <div>
              <h1 className="text-2xl font-bold">Fitness Goals</h1>
              <p className="text-sm text-muted-foreground">Body and performance targets synced with your workout & health logs</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Link href="/goals">
              <Button variant="outline" size="sm" className="hidden sm:flex items-center gap-1.5">
                <ExternalLink className="w-3.5 h-3.5" />
                All Goals
              </Button>
            </Link>
            <Button onClick={() => setDialogState({ open: true, goal: null })} className="flex items-center gap-1.5">
              <Plus className="w-4 h-4" />
              Add Goal
            </Button>
          </div>
        </div>

        {/* Today's snapshot */}
        <TodayPanel today={today} trackedKeys={usedMetrics} />

        {/* Loading */}
        {isLoading && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {[1, 2].map(i => (
              <div key={i} className="h-52 rounded-xl bg-muted animate-pulse" />
            ))}
          </div>
        )}

        {/* Goals grid */}
        {!isLoading && fitnessGoals.length > 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {fitnessGoals.map(goal => (
              <GoalCard
                key={goal.id}
                goal={goal}
                todayValue={getTodayVal((goal as any).healthMetric as FitnessMetricKey)}
                trendData={getTrend((goal as any).healthMetric as FitnessMetricKey)}
                onEdit={openEdit}
                onDelete={id => deleteMutation.mutate(id)}
                onShare={setSharingGoal}
              />
            ))}
          </div>
        )}

        {/* Empty state */}
        {!isLoading && fitnessGoals.length === 0 && (
          <div className="flex flex-col items-center justify-center py-20 text-center">
            <div className="p-4 rounded-full bg-orange-100 dark:bg-orange-900/20 mb-4">
              <Dumbbell className="w-10 h-10 text-orange-500" />
            </div>
            <h2 className="text-xl font-semibold mb-2">No fitness goals yet</h2>
            <p className="text-muted-foreground max-w-xs mb-6">
              Set targets for workouts, steps, weight, sleep and more — progress syncs automatically from your health logs.
            </p>
            <Button onClick={() => setDialogState({ open: true, goal: null })} className="flex items-center gap-2">
              <Plus className="w-4 h-4" />
              Create your first fitness goal
            </Button>
          </div>
        )}

        {/* Info footer */}
        {fitnessGoals.length > 0 && (
          <p className="text-center text-xs text-muted-foreground mt-8">
            Progress syncs from your{" "}
            <Link href="/workout-planner" className="underline underline-offset-2">Workout Planner</Link>
            {" "}and{" "}
            <Link href="/health-metrics" className="underline underline-offset-2">Health Metrics</Link>.
            Fitness goals also appear on the{" "}
            <Link href="/goals" className="underline underline-offset-2">Goals</Link> page.
          </p>
        )}
      </div>

      {/* Dialog */}
      {dialogState.open && (
        <GoalDialog
          state={dialogState}
          onClose={closeDialog}
          usedMetrics={usedMetrics}
        />
      )}

      {sharingGoal && (
        <ShareDialog
          isOpen={!!sharingGoal}
          onClose={() => setSharingGoal(null)}
          itemId={sharingGoal.id}
          itemType="fitness_goal"
          itemTitle={sharingGoal.title}
        />
      )}
    </div>
  );
}
